import React from 'react';
import { Link } from '@inertiajs/react';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable, { type Column } from '../../components/admin/DataTable';
import FilterBar, { submitFilter } from '../../components/admin/FilterBar';
import { day } from '../../components/admin/format';

type Row = {
  id: number;
  title: string;
  subtitle: string | null;
  user_email: string | null;
  state: 'active' | 'trashed' | 'archived' | null;
  at: string;
};

type ContentProps = {
  kind: string;
  rows: Row[];
  q: string;
  state: string;
  user_id: string | null;
  user_email: string | null;
  page: number;
  per: number;
  total: number;
};

type ContentComponent = React.FC<ContentProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const KINDS: [string, string][] = [
  ['tasks', '任务'],
  ['wiki', 'Wiki'],
  ['worklogs', 'Worklog'],
  ['projects', '项目'],
];

// 第二列的表头随 kind 变 —— 四种内容第二重要的东西不一样。
const SUBTITLE_HEADER: Record<string, string> = {
  tasks: '项目',
  wiki: '标签',
  worklogs: '类型',
  projects: '客户',
};

const STATE_PILL: Record<string, [string, string]> = {
  active: ['活跃', 'bg-admin-okSoft text-admin-ok'],
  trashed: ['回收站', 'bg-admin-dangerSoft text-admin-danger'],
  archived: ['已归档', 'bg-admin-surface2 text-admin-muted'],
};

// worklogs 没有软删，projects 的档位是归档 —— 状态筛选按 kind 给不同的选项。
const STATE_OPTIONS: Record<string, [string, string][]> = {
  tasks: [
    ['all', '状态：全部'],
    ['active', '状态：活跃'],
    ['trashed', '状态：回收站'],
  ],
  wiki: [
    ['all', '状态：全部'],
    ['active', '状态：活跃'],
    ['trashed', '状态：回收站'],
  ],
  projects: [
    ['all', '状态：全部'],
    ['active', '状态：活跃'],
    ['archived', '状态：已归档'],
  ],
  worklogs: [],
};

function columnsFor(kind: string): Column<Row>[] {
  const cols: Column<Row>[] = [
    { key: 'id', header: 'ID', width: '58px', align: 'right', mono: true, render: (r) => r.id },
    {
      key: 'title',
      header: '标题',
      truncate: true,
      render: (r) => (
        <Link
          href={`/admin/content/${kind}/${r.id}`}
          className={
            r.state === 'trashed'
              ? 'text-admin-muted line-through hover:underline'
              : 'text-admin-accent hover:underline'
          }
        >
          {r.title}
        </Link>
      ),
    },
    {
      key: 'user',
      header: '归属',
      width: '190px',
      truncate: true,
      render: (r) =>
        r.user_email ? (
          <span className="text-admin-ink2">{r.user_email}</span>
        ) : (
          <span className="text-admin-muted">—</span>
        ),
    },
    {
      key: 'subtitle',
      header: SUBTITLE_HEADER[kind] ?? '—',
      width: '140px',
      truncate: true,
      render: (r) => r.subtitle ?? <span className="text-admin-muted">—</span>,
    },
  ];

  if (kind !== 'worklogs') {
    cols.push({
      key: 'state',
      header: '状态',
      width: '78px',
      render: (r) => {
        const pill = r.state ? STATE_PILL[r.state] : null;
        if (!pill) return null;
        return (
          <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${pill[1]}`}>
            {pill[0]}
          </span>
        );
      },
    });
  }

  cols.push({
    key: 'at',
    header: kind === 'worklogs' ? '完成于' : '更新',
    width: '104px',
    render: (r) => <span className="text-admin-muted">{day(r.at)}</span>,
  });

  return cols;
}

const Content: ContentComponent = ({
  kind,
  rows,
  q,
  state,
  user_id,
  user_email,
  page,
  per,
  total,
}) => (
  <>
    <h1 className="mb-3 text-[14.5px] font-semibold tracking-tight">
      <span className="font-normal text-admin-muted">内容巡检 / </span>
      {KINDS.find(([k]) => k === kind)?.[1]}
    </h1>

    <div className="mb-3 flex gap-0.5 border-b border-admin-line">
      {KINDS.map(([k, label]) => (
        <Link
          key={k}
          href={`/admin/content/${k}`}
          className={`-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] ${
            k === kind
              ? 'border-admin-accent font-semibold text-admin-accent'
              : 'border-transparent text-admin-ink2 hover:text-admin-ink'
          }`}
        >
          {label}
        </Link>
      ))}
    </div>

    <FilterBar q={q} placeholder="搜索标题…" right={`${total} 条`}>
      {user_id && (
        <span className="inline-flex h-[30px] items-center gap-1 rounded-md border border-admin-accent/30 bg-admin-accentSoft pl-2.5 pr-1 text-[12.5px] text-admin-accent">
          归属：{user_email ?? user_id}
          <button
            type="button"
            title="清除该筛选"
            onClick={() => submitFilter({ user_id: null })}
            className="px-1 text-[15px] leading-none opacity-65 hover:opacity-100"
          >
            ×
          </button>
        </span>
      )}
      {STATE_OPTIONS[kind]?.length > 0 && (
        <select
          value={state}
          onChange={(e) => submitFilter({ state: e.target.value })}
          className="h-[30px] rounded-md border border-admin-line2 bg-admin-surface px-2 text-[12.5px]"
        >
          {STATE_OPTIONS[kind].map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      )}
    </FilterBar>

    <DataTable
      columns={columnsFor(kind)}
      rows={rows}
      rowKey={(r) => r.id}
      rowClassName={(r) => (r.state === 'trashed' ? '[&>td]:text-admin-muted' : '')}
      empty="没有匹配的记录"
      pagination={{ page, per, total }}
    />

    {kind !== 'worklogs' && (
      <div className="mt-3 max-w-[94ch] rounded-md border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
        「状态：全部」会绕过 <code className="font-mono">default_scope</code>。Task 与 WikiPage
        都有 <code className="font-mono">default_scope {'{'} where(deleted_at: nil) {'}'}</code>
        ，巡检要看得见回收站里的行，否则一半的排查场景一开始就瞎了。绕过只在{' '}
        <code className="font-mono">Admin::ContentQuery</code> 一个文件里做。
      </div>
    )}
  </>
);

Content.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default Content;
